// datetime.js — een tijdstempel uit de database tonen.
//
// De backend bewaart tijden in UTC (D1 CURRENT_TIMESTAMP / datetime('now')), meestal als
// "YYYY-MM-DD HH:MM:SS" zonder zone. JavaScript zou dat als LOKALE tijd lezen, en dat is fout;
// parseTimestamp plakt er daarom een 'Z' op.
//
// Tonen doen we in de tijdzone van de kijker. Hier stond eerst Europe/Amsterdam vast, met de
// gedachte "dan ziet iedereen dezelfde klok". Die gedachte klopt niet zodra iemand ergens
// anders staat: een collega die in Toronto om 17:00 een boring maakte zag er 23:00 bij staan --
// het juiste moment, in de verkeerde stad. Een tijdstempel hoort te zeggen hoe laat het wás
// waar je bent.
//
// De notatie blijft Nederlands (dag-maand-jaar, 24 uur); alleen de klok volgt de kijker.
//
// undefined = de tijdzone van de browser. Dit is de plek om er ooit een voorkeur per gebruiker
// of per project van te maken; één constante, niet twintig aanroepen.
const TZ = undefined;

/** Parse a DB/ISO timestamp into a Date, assuming UTC when no timezone is given. */
export function parseTimestamp(value) {
  if (!value) return null;
  if (value instanceof Date) return isNaN(value) ? null : value;
  let s = String(value).trim().replace(' ', 'T');
  // No 'Z' and no +hh:mm offset → it's a bare UTC timestamp from the DB.
  if (!/[zZ]$|[+-]\d{2}:?\d{2}$/.test(s)) s += 'Z';
  const d = new Date(s);
  return isNaN(d) ? null : d;
}

/** Datum + tijd in de tijdzone van de kijker, bv. "17-06-2026 00:28". */
export function formatDateTime(value, opts = {}) {
  const d = parseTimestamp(value);
  if (!d) return '—';
  return d.toLocaleString('nl-NL', {
    timeZone: TZ, day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit', ...opts,
  });
}

/**
 * Date + time met schuine strepen, bv. "17/06/2026 00:28".
 *
 * Apart van formatDateTime: die staat met streepjes op tientallen plekken in beeld en die
 * wilden we niet allemaal laten verspringen. Opgebouwd uit losse delen i.p.v. tekst
 * vervangen, zodat een locale-eigenaardigheid het formaat niet stilletjes verandert.
 */
export function formatDateTimeSlash(value) {
  const d = parseTimestamp(value);
  if (!d) return '—';
  const p = Object.fromEntries(new Intl.DateTimeFormat('nl-NL', {
    timeZone: TZ, day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit', hour12: false,
  }).formatToParts(d).map((x) => [x.type, x.value]));
  return `${p.day}/${p.month}/${p.year} ${p.hour}:${p.minute}`;
}

/** Alleen de datum, in de tijdzone van de kijker, bv. "17 jun 2026". */
export function formatDate(value, opts = {}) {
  const d = parseTimestamp(value);
  if (!d) return '—';
  return d.toLocaleDateString('nl-NL', {
    timeZone: TZ, day: '2-digit', month: 'short', year: 'numeric', ...opts,
  });
}

/** Alleen de tijd, in de tijdzone van de kijker, bv. "00:28". */
export function formatTime(value, opts = {}) {
  const d = parseTimestamp(value);
  if (!d) return '—';
  return d.toLocaleTimeString('nl-NL', { timeZone: TZ, hour: '2-digit', minute: '2-digit', ...opts });
}
